import React, { useState } from 'react';
import { View, Pressable, Text, StyleSheet, Image } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { Play } from 'lucide-react';
import { Series } from '../types/domain';
import { NETFLIX_COLORS, NETFLIX_SPACING } from '../theme/netflixTheme';

interface SeriesGridProps {
  title: string;
  series: Series[];
  isLoading?: boolean;
  horizontal?: boolean;
}

export function SeriesGrid({
  title,
  series,
  isLoading = false,
  horizontal = false,
}: SeriesGridProps) {
  const navigate = useNavigate();
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <View style={styles.section}>
        <Text style={styles.title}>{title}</Text>
        <div style={skeletonGridStyle}>
          {Array.from({ length: 6 }).map((_, i) => (
            <View key={i} style={styles.skeletonCard} />
          ))}
        </div>
      </View>
    );
  }

  return (
    <View style={styles.section}>
      <Text style={styles.title}>{title}</Text>
      {horizontal ? (
        <div style={horizontalGridStyle}>
          {series.map((s) => (
            <SeriesCard
              key={s.id}
              series={s}
              isHovered={hoveredId === s.id}
              onPress={() => navigate(`/series/${s.id}`)}
              onHoverIn={() => setHoveredId(s.id)}
              onHoverOut={() => setHoveredId(null)}
            />
          ))}
        </div>
      ) : (
        <div style={gridStyle}>
          {series.map((s) => (
            <SeriesCard
              key={s.id}
              series={s}
              isHovered={hoveredId === s.id}
              onPress={() => navigate(`/series/${s.id}`)}
              onHoverIn={() => setHoveredId(s.id)}
              onHoverOut={() => setHoveredId(null)}
            />
          ))}
        </div>
      )}
    </View>
  );
}

interface SeriesCardProps {
  series: Series;
  isHovered: boolean;
  onPress: () => void;
  onHoverIn: () => void;
  onHoverOut: () => void;
}

function SeriesCard({ series, isHovered, onPress, onHoverIn, onHoverOut }: SeriesCardProps) {
  const isComingSoon = series.status === 'ongoing';

  return (
    <Pressable
      style={styles.card}
      onPress={onPress}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
    >
      <View style={styles.posterContainer}>
        {series.poster_url ? (
          <Image
            source={{ uri: series.poster_url }}
            style={styles.seriesPoster}
          />
        ) : (
          <View style={[styles.seriesPoster, styles.posterPlaceholder]}>
            <Text style={styles.posterIcon}>—</Text>
          </View>
        )}
        {isComingSoon && (
          <View style={styles.comingSoonBadge}>
            <Text style={styles.comingSoonText}>EM BREVE</Text>
          </View>
        )}
        {isHovered && (
          <View style={styles.hoverOverlay}>
            <View style={styles.hoverContent}>
              <Play size={48} color={NETFLIX_COLORS.primary} />
              <Text style={styles.rating}>{series.rating.toFixed(1)}</Text>
            </View>
          </View>
        )}
      </View>
      <View style={styles.cardInfo}>
        <Text style={styles.cardTitle} numberOfLines={2}>
          {series.title}
        </Text>
        <Text style={styles.cardYear}>{series.release_year}</Text>
      </View>
    </Pressable>
  );
}

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
  gap: `${NETFLIX_SPACING.lg}px`,
};

const horizontalGridStyle: React.CSSProperties = {
  display: 'flex',
  gap: `${NETFLIX_SPACING.lg}px`,
  overflowX: 'auto',
};

const skeletonGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
  gap: `${NETFLIX_SPACING.lg}px`,
  marginTop: NETFLIX_SPACING.lg,
};

const styles = StyleSheet.create({
  section: {
    marginBottom: NETFLIX_SPACING.xxxl,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: NETFLIX_COLORS.white,
    marginBottom: NETFLIX_SPACING.lg,
  },
  card: {
    width: '100%',
    borderRadius: 8,
    overflow: 'hidden',
  },
  posterContainer: {
    position: 'relative',
  },
  seriesPoster: {
    width: '100%',
    aspectRatio: 2 / 3,
    borderRadius: 8,
    backgroundColor: NETFLIX_COLORS.surface,
  },
  posterHovered: {
    opacity: 0.9,
  },
  posterPlaceholder: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  posterIcon: {
    fontSize: 48,
    color: NETFLIX_COLORS.textTertiary,
  },
  comingSoonBadge: {
    position: 'absolute',
    top: NETFLIX_SPACING.md,
    right: NETFLIX_SPACING.md,
    backgroundColor: '#FF6B6B',
    paddingHorizontal: NETFLIX_SPACING.md,
    paddingVertical: NETFLIX_SPACING.sm,
    borderRadius: 4,
    zIndex: 5,
  },
  comingSoonText: {
    color: NETFLIX_COLORS.white,
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  hoverOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
  },
  hoverContent: {
    alignItems: 'center',
    gap: NETFLIX_SPACING.lg,
  },
  rating: {
    color: NETFLIX_COLORS.white,
    fontSize: 14,
    fontWeight: '700',
  },
  cardInfo: {
    paddingTop: NETFLIX_SPACING.md,
  },
  cardTitle: {
    color: NETFLIX_COLORS.white,
    fontSize: 13,
    fontWeight: '500',
    lineHeight: 18,
    marginBottom: NETFLIX_SPACING.sm,
  },
  cardYear: {
    color: NETFLIX_COLORS.textTertiary,
    fontSize: 11,
    fontWeight: '400',
  },
  skeletonCard: {
    width: '100%',
    aspectRatio: 2 / 3,
    backgroundColor: NETFLIX_COLORS.surface,
    borderRadius: 8,
  },
});
